// Export types
export type { MaskType, MaskConfig, MaskTypeOption } from './types';

// Export constants
export { MASK_PRESETS, MASK_LABELS, NO_SPACES_MASK } from './const';

// Import internal dependencies
import type { MaskType, MaskConfig, MaskTypeOption } from './types';
import { MASK_PRESETS, MASK_LABELS } from './const';

/**
 * Get mask configuration for a given mask type
 *
 * @param maskType - The type of mask to apply
 * @param customMask - Custom mask pattern (required if maskType is 'custom')
 * @param eager - Whether to apply mask eagerly (default: false)
 * @returns Mask configuration object or null if no mask should be applied
 *
 */
export function getMaskConfig(
  maskType: MaskType | undefined,
  customMask?: string,
  eager = false
): MaskConfig | null {
  if (!maskType || maskType === 'none') {
    return null;
  }

  if (maskType === 'custom') {
    if (!customMask) {
      return null;
    }
    return {
      mask: customMask,
      eager,
    };
  }

  return {
    mask: MASK_PRESETS[maskType],
    eager,
  };
}

/**
 * Get all available mask types for dropdown/select components
 *
 * @returns Array of mask type options with value and label
 */
export function getMaskTypeOptions(): MaskTypeOption[] {
  return Object.entries(MASK_LABELS).map(([value, label]) => ({
    value: value as MaskType,
    label,
  }));
}

/**
 * Get the mask pattern for a given mask type
 *
 * @param maskType - The type of mask
 * @param customMask - Custom mask pattern (required if maskType is 'custom')
 * @returns The mask pattern string or null
 */
export function getMaskPattern(maskType: MaskType | undefined, customMask?: string): string | null {
  if (!maskType || maskType === 'none') {
    return null;
  }

  if (maskType === 'custom') {
    return customMask || null;
  }

  return MASK_PRESETS[maskType];
}

/**
 * Get the label for a mask type
 *
 * @param maskType - The type of mask
 * @returns The human-readable label
 */
export function getMaskLabel(maskType: MaskType): string {
  return MASK_LABELS[maskType];
}

/**
 * Validates if a mask pattern is valid
 *
 * @param pattern - The mask pattern to validate
 * @returns True if the pattern is valid
 */
export function isValidMaskPattern(pattern: string): boolean {
  if (!pattern) return false;
  // Basic validation: check if pattern contains valid mask characters
  const validChars = /^[#@\s\-()./:]*$/;
  return validChars.test(pattern);
}
